import { bopApi } from '@/lib/api/client';
import type { WsTrade } from '@/lib/websocket';

const TRADES_PATH = 'v1/trades';

/** Fixed server constant (bop-api getTrades LIMIT) — the client cannot change it, no page-size UI. */
export const TRADES_PAGE_SIZE = 100;

export const TRADE_SIDES = ['BUY', 'SELL'] as const;
export type TradeSide = (typeof TRADE_SIDES)[number];

/** REST trade from POST /v1/trades — amount/price are NUMBERS here (strings on the WS feed). */
export interface Trade {
  /** '' when bop-api's fastId→accountHolder Dynamo enrichment failed */
  accountHolderId: string;
  /** 'BTC_EUR' */
  instrument: string;
  amount: number;
  price: number;
  side: TradeSide;
  /** ISO 8601 */
  time: string;
  tradeId: string;
  /** Returned but never displayed — kept for a future detail view */
  orderId: string;
}

/**
 * One row model for REST pages + live WS rows: WS rows carry no
 * orderId, so it is optional here; WS string amounts/prices are normalized
 * to numbers by wsTradeToRow so the shared columns take one shape.
 */
export interface TradeRow extends Omit<Trade, 'orderId'> {
  orderId?: string;
}

/** Flat wire body for POST /v1/trades and /v1/user/{pid}/trades. */
export interface TradesWireRequest {
  /** Opaque base64 keyset cursor — roundtrip verbatim, never inspect */
  cursor?: string;
  /** ISO 8601 */
  from?: string;
  /** ISO 8601; server substitutes "now" when absent */
  to?: string;
  fromPrice?: number;
  toPrice?: number;
  instrument?: string[];
  side?: TradeSide;
  tradeId?: string[];
}

export interface TradesWireResponse {
  trades: Trade[];
  /** Present ⇔ more pages */
  cursor?: string;
}

export interface TradePriceRange {
  from?: number;
  to?: number;
}

/** ISO datetime strings (local start/end-of-day serialized with the operator's offset) */
export interface TradeTimeRange {
  from?: string;
  to?: string;
}

/** UI-side nested filter shape (filter dialog output). */
export interface TradesFilters {
  accountHolderId?: string;
  instrument?: string[];
  price?: TradePriceRange;
  side?: TradeSide;
  time?: TradeTimeRange;
  tradeId?: string[];
}

/**
 * Filter fields shared by exchange trades and instant trades: the conditional
 * spreads common to both wire bodies — buildInstantTradesRequestBody composes
 * these with its lek.
 */
export const buildTradeFilterFields = (
  filters: Pick<TradesFilters, 'instrument' | 'side' | 'tradeId' | 'time'>,
): Pick<TradesWireRequest, 'from' | 'instrument' | 'side' | 'to' | 'tradeId'> => ({
  ...(filters.instrument && filters.instrument.length > 0 ? { instrument: filters.instrument } : {}),
  ...(filters.side ? { side: filters.side } : {}),
  ...(filters.tradeId && filters.tradeId.length > 0 ? { tradeId: filters.tradeId } : {}),
  ...(filters.time?.from ? { from: filters.time.from } : {}),
  ...(filters.time?.to ? { to: filters.time.to } : {}),
});

/**
 * Nested UI filters → flat wire body (moved from the Nuxt BFF).
 * `accountHolderId` is deliberately NOT part of the body — it only selects the
 * endpoint (see getTradesPage).
 */
export const buildTradesRequestBody = (filters: TradesFilters, cursor?: string): TradesWireRequest => ({
  ...buildTradeFilterFields(filters),
  // Legacy `x ? x : undefined` quirk kept: a price bound of 0 is dropped here —
  // and the server would ignore it anyway
  ...(filters.price?.from ? { fromPrice: filters.price.from } : {}),
  ...(filters.price?.to ? { toPrice: filters.price.to } : {}),
  ...(cursor ? { cursor } : {}),
});

export interface GetTradesPageParams {
  filters: TradesFilters;
  cursor?: string;
}

/**
 * The Account Holder ID filter swaps ENDPOINTS instead of filtering the body:
 * the per-user endpoint scopes to the holder PLUS subaccounts
 * (broker-aware), so returned accountHolderId values may differ from the
 * filtered id — displayed as-is, deliberately not client-filtered.
 */
export const getTradesPage = ({ filters, cursor }: GetTradesPageParams): Promise<TradesWireResponse> => {
  const body = buildTradesRequestBody(filters, cursor);
  return filters.accountHolderId
    ? bopApi.post(`v1/user/${filters.accountHolderId}/trades`, { json: body }).json<TradesWireResponse>()
    : bopApi.post(TRADES_PATH, { json: body }).json<TradesWireResponse>();
};

/**
 * Live WS row → shared row model: wire amount/price are STRINGS on
 * the socket but numbers on REST — normalized here. orderId is intentionally
 * omitted: the wire never sends it for instant trades.
 */
export const wsTradeToRow = (trade: WsTrade): TradeRow => ({
  accountHolderId: trade.accountHolderId,
  instrument: trade.instrument,
  amount: Number(trade.amount),
  price: Number(trade.price),
  side: trade.side,
  time: trade.time,
  tradeId: trade.tradeId,
});

/** Only the FIRST '_' becomes '/' — 'BTC_USD_P' renders 'BTC/USD_P' (legacy quirk kept). */
export const formatInstrumentPair = (instrument: string): string => instrument.replace('_', '/');

/** 'BTC_USD_P' → { base: 'BTC', quote: 'USD' } — icon codes come from the first two segments. */
export const splitInstrument = (instrument: string): { base: string; quote: string } => {
  const [base = '', quote = ''] = instrument.split('_');
  return { base, quote };
};
